/* =====================================================
   AE·ORA Micro-Interactions Layer — aeora-micro.js
   Deferred. Zero-dep. Respects prefers-reduced-motion.
   ----------------------------------------------------
   Features:
     1. Smart number counters    [auto: stat classes + [data-counter]]
     2. Blur-reveal headings     [.reveal-blur, .reveal-blur-words]
     3. Top scroll-progress bar
     4. Magnetic buttons         [.btn-magnetic]
     5. Cursor-tilt cards        [.card-tilt]
     6. Parallax images          [data-parallax="0.15"]
     7. Page-fade on link nav    [internal links only]
===================================================== */
(function(){
  'use strict';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─────────────────────────────────────────────────
     1. SMART NUMBER COUNTERS
     Auto-detects elements containing exactly one number
     (e.g. "4.9", "50%", "2-4wk", "500mg", "900+"). Skips
     prices ($), non-numeric ("ISO"), and elements with
     class .no-count or data-counter="false".
  ───────────────────────────────────────────────── */
  var COUNTER_SELECTORS = [
    '[data-counter]',
    '.sa-stat-num',           '.sa-stat-number',
    '.home-renew__stat-number',
    '.pdp-proof-stat-number', '.pdp-proof-num', '.pdp-proof-stat',
    '.proof-stat-number',     '.proof-stat-num',
    '.sci-stat-number',       '.sci-stat-num',
    '.review-stat-number',    '.review-stat-num',
    '.hr-stat-number',        '.hr-stat-num',
    '.hg-stat-number',        '.hg-stat-num',
    '.about-stat-number',     '.about-stat-num',
    '.faq-stat-number',
    '.sa-pill-ct',
    '.aeora-stat',            '.stat-number'
  ].join(',');

  function parseTarget(raw){
    if(!raw) return null;
    var txt = raw.trim();
    if(txt.charAt(0) === '$') return null;                     // skip prices
    if(/no[-_]?count|disabled/i.test(txt)) return null;
    var m = txt.match(/^(\D*?)([\d.,]+)([\s\S]*)$/);           // first numeric run
    if(!m) return null;
    var numStr = m[2].replace(/,/g,'');
    var num = parseFloat(numStr);
    if(!isFinite(num) || num <= 0) return null;
    var decimals = (numStr.split('.')[1]||'').length;
    return { prefix: m[1], suffix: m[3], num: num, decimals: decimals, raw: txt };
  }
  function formatNum(v, decimals){
    if(decimals > 0) return v.toFixed(decimals);
    return Math.round(v).toLocaleString();
  }
  function easeOutExpo(t){ return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); }
  function animateCounter(el, target){
    var duration = 1600;
    var start = performance.now();
    function tick(now){
      var p = Math.min(1, (now - start) / duration);
      var eased = easeOutExpo(p);
      var v = target.num * eased;
      el.textContent = target.prefix + formatNum(v, target.decimals) + target.suffix;
      if(p < 1) requestAnimationFrame(tick);
      else el.textContent = target.raw;
    }
    requestAnimationFrame(tick);
  }
  function initCounters(){
    var els = document.querySelectorAll(COUNTER_SELECTORS);
    if(!els.length) return;
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if(!e.isIntersecting) return;
        var el = e.target;
        if(el.dataset.counterRan === '1') { io.unobserve(el); return; }
        var target = parseTarget(el.dataset.counter || el.textContent);
        if(!target){ io.unobserve(el); return; }
        el.dataset.counterRan = '1';
        if(reduced) { el.textContent = target.raw; io.unobserve(el); return; }
        animateCounter(el, target);
        io.unobserve(el);
      });
    }, { threshold: 0.4, rootMargin: '0px 0px -60px 0px' });
    els.forEach(function(el){ io.observe(el); });
  }

  /* ─────────────────────────────────────────────────
     2. BLUR-REVEAL HEADINGS
     .reveal-blur          → whole element develops in
     .reveal-blur-words    → split by words, staggered
  ───────────────────────────────────────────────── */
  function initBlurReveal(){
    if(reduced) {
      document.querySelectorAll('.reveal-blur,.reveal-blur-words').forEach(function(el){
        el.classList.add('is-revealed');
      });
      return;
    }
    // Word splitting, only safe when element is plain text (no child tags)
    document.querySelectorAll('.reveal-blur-words').forEach(function(el){
      if(el.dataset.split === '1') return;
      el.dataset.split = '1';
      // Bail to plain blur reveal if the element contains HTML (br, span, etc.)
      var hasChildren = false;
      for(var i=0;i<el.childNodes.length;i++){
        if(el.childNodes[i].nodeType !== 3){ hasChildren = true; break; }
      }
      if(hasChildren){
        el.classList.remove('reveal-blur-words');
        el.classList.add('reveal-blur');
        return;
      }
      var text = el.textContent;
      var frag = document.createDocumentFragment();
      text.replace(/(\S+)(\s+|$)/g, function(_, word, ws) {
        var outer = document.createElement('span'); outer.className = 'rbw-word';
        var inner = document.createElement('span'); inner.className = 'rbw-inner';
        inner.textContent = word;
        outer.appendChild(inner);
        frag.appendChild(outer);
        if(ws) frag.appendChild(document.createTextNode(ws));
      });
      el.innerHTML = '';
      el.appendChild(frag);
      var words = el.querySelectorAll('.rbw-word');
      words.forEach(function(w, i){ w.style.transitionDelay = (i * 60) + 'ms'; });
    });

    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if(e.isIntersecting){
          e.target.classList.add('is-revealed');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    document.querySelectorAll('.reveal-blur,.reveal-blur-words').forEach(function(el){ io.observe(el); });
  }

  /* ─────────────────────────────────────────────────
     2b. AUTO-REVEAL within .ae-auto-reveal regions
     Targets headings, paragraphs, stats, cards, images
     and applies a staggered fade-up on intersection.
  ───────────────────────────────────────────────── */
  var AUTO_REVEAL_SELECTORS = [
    'h2','h3','h4',
    'p',
    'figure','img',
    'blockquote',
    '.sci-stat','.sci-stat-card','.sci-card','.sci-ing','.sci-stack-card',
    '.about-stat','.about-card','.about-tile','.about-row',
    '.os-stat','.os-tile','.os-card','.os-row',
    'li',
    '.coll-evidence','.coll-card'
  ].join(',');
  function initAutoReveal(){
    var regions = document.querySelectorAll('.ae-auto-reveal');
    if(!regions.length) return;
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if(!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -10% 0px' });
    regions.forEach(function(region){
      var nodes = region.querySelectorAll(AUTO_REVEAL_SELECTORS);
      // Filter to direct descendants of region — avoids tagging text inside
      // already-tagged ancestors.
      var seen = [];
      nodes.forEach(function(el){
        // skip if ancestor already marked
        var p = el.parentElement;
        while(p && p !== region){
          if(p.hasAttribute('data-r')) return;
          p = p.parentElement;
        }
        // skip headings inside p, etc.
        if(el.closest('[data-r]') && el.closest('[data-r]') !== el) return;
        seen.push(el);
      });
      // Group by visual row to stagger nicely
      seen.forEach(function(el, i){
        el.setAttribute('data-r','');
        var delay = Math.min(800, i * 70);
        el.style.setProperty('--d', delay + 'ms');
        io.observe(el);
      });
    });
  }

  /* ─────────────────────────────────────────────────
     3. TOP SCROLL-PROGRESS BAR
  ───────────────────────────────────────────────── */
  function initScrollProgress(){
    if(reduced) return;
    var bar = document.createElement('div');
    bar.className = 'ae-scroll-progress';
    bar.innerHTML = '<span class="ae-scroll-progress-fill"></span>';
    document.body.appendChild(bar);
    var fill = bar.firstElementChild;
    var ticking = false;
    function tick(){
      var h = document.documentElement;
      var max = h.scrollHeight - h.clientHeight;
      var p = max > 0 ? (h.scrollTop || window.scrollY) / max : 0;
      fill.style.transform = 'scaleX(' + Math.max(0, Math.min(1, p)) + ')';
      bar.classList.toggle('is-active', p > 0.008 && p < 0.995);
      ticking = false;
    }
    window.addEventListener('scroll', function(){
      if(!ticking){ ticking = true; requestAnimationFrame(tick); }
    }, { passive: true });
    tick();
  }

  /* Legacy section reveals are centralised here so product pages do not
     register a second observer/controller. */
  function initLegacyReveal(){
    var els = document.querySelectorAll('.will-reveal,.reveal-left,.reveal-right');
    if(!els.length) return;
    if(reduced){ els.forEach(function(el){ el.classList.add('revealed'); }); return; }
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(entry){
        if(!entry.isIntersecting) return;
        entry.target.classList.add('revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin:'0px 0px -8% 0px', threshold:0.08 });
    els.forEach(function(el){ io.observe(el); });
  }

  /* ─────────────────────────────────────────────────
     4. MAGNETIC BUTTONS
  ───────────────────────────────────────────────── */
  function initMagnetic(){
    if(reduced) return;
    var radius = 80;          // distance from cursor at which pull begins
    var strength = 0.32;      // how strongly the button moves
    var els = document.querySelectorAll('.btn-magnetic');
    els.forEach(function(el){
      var rafId = null, currentX = 0, currentY = 0, targetX = 0, targetY = 0;
      function loop(){
        currentX += (targetX - currentX) * 0.18;
        currentY += (targetY - currentY) * 0.18;
        el.style.transform = 'translate3d('+currentX.toFixed(2)+'px,'+currentY.toFixed(2)+'px,0)';
        if(Math.abs(targetX - currentX) > 0.05 || Math.abs(targetY - currentY) > 0.05){
          rafId = requestAnimationFrame(loop);
        } else {
          rafId = null;
        }
      }
      el.addEventListener('mousemove', function(e){
        var r = el.getBoundingClientRect();
        var cx = r.left + r.width/2, cy = r.top + r.height/2;
        var dx = e.clientX - cx, dy = e.clientY - cy;
        var dist = Math.sqrt(dx*dx + dy*dy);
        if(dist < radius + Math.max(r.width,r.height)/2){
          targetX = dx * strength;
          targetY = dy * strength;
          if(!rafId) rafId = requestAnimationFrame(loop);
        }
      });
      el.addEventListener('mouseleave', function(){
        targetX = 0; targetY = 0;
        if(!rafId) rafId = requestAnimationFrame(loop);
      });
    });
  }

  /* ─────────────────────────────────────────────────
     5. CURSOR-TILT CARDS
  ───────────────────────────────────────────────── */
  function initTilt(){
    if(reduced) return;
    var maxTilt = 4;          // degrees
    var els = document.querySelectorAll('.card-tilt');
    els.forEach(function(el){
      var rafId = null, rx = 0, ry = 0, tx = 0, ty = 0;
      function loop(){
        rx += (tx - rx) * 0.18;
        ry += (ty - ry) * 0.18;
        el.style.transform = 'perspective(900px) rotateX('+rx.toFixed(2)+'deg) rotateY('+ry.toFixed(2)+'deg)';
        if(Math.abs(tx - rx) > 0.02 || Math.abs(ty - ry) > 0.02){
          rafId = requestAnimationFrame(loop);
        } else { rafId = null; }
      }
      el.style.willChange = 'transform';
      el.addEventListener('mousemove', function(e){
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        tx = (px - 0.5) * 2 * maxTilt;
        ty = (0.5 - py) * 2 * maxTilt;
        if(!rafId) rafId = requestAnimationFrame(loop);
      });
      el.addEventListener('mouseleave', function(){
        tx = 0; ty = 0;
        if(!rafId) rafId = requestAnimationFrame(loop);
      });
    });
  }

  /* ─────────────────────────────────────────────────
     6. PARALLAX IMAGES (data-parallax="0.12")
  ───────────────────────────────────────────────── */
  function initParallax(){
    if(reduced) return;
    var els = document.querySelectorAll('[data-parallax]');
    if(!els.length) return;
    var entries = [];
    els.forEach(function(el){
      var amount = parseFloat(el.dataset.parallax) || 0.12;
      el.style.willChange = 'transform';
      entries.push({ el: el, amount: amount });
    });
    var ticking = false;
    function tick(){
      var vh = window.innerHeight;
      entries.forEach(function(e){
        var r = e.el.getBoundingClientRect();
        var center = r.top + r.height/2;
        var offset = (center - vh/2) * e.amount * -1;
        e.el.style.transform = 'translate3d(0,'+offset.toFixed(2)+'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function(){
      if(!ticking){ ticking = true; requestAnimationFrame(tick); }
    }, { passive: true });
    tick();
  }

  /* ─────────────────────────────────────────────────
     7. INSTANT NAVIGATION
     - Removes the artificial fade-out delay that made clicks feel sluggish.
     - Adds a thin top loading bar while the next page is requested.
     - Prefetches same-origin pages on hover/touchstart so they're already
       in the browser cache by the time the user clicks (perceived instant).
     - Keeps the gentle fade-in on the new page (CSS keyframe, no JS delay).
  ───────────────────────────────────────────────── */
  function initInstantNav(){
    if(reduced) return;
    document.body.classList.add('ae-page-fade-in');

    /* Build a thin top loading bar */
    var navbar = document.createElement('div');
    navbar.className = 'ae-nav-loader';
    document.body.appendChild(navbar);
    var navState = { active:false, timer:null };
    function startLoader(){
      if(navState.active) return;
      navState.active = true;
      navbar.classList.remove('done');
      // Animate to 80% over ~6s, then wait for navigation to complete it
      navbar.style.transform = 'scaleX(0)';
      requestAnimationFrame(function(){
        requestAnimationFrame(function(){
          navbar.style.transition = 'transform 6s cubic-bezier(.1,.8,.2,1)';
          navbar.style.transform = 'scaleX(0.8)';
        });
      });
    }

    /* Prefetch on hover / touchstart — same-origin only */
    var prefetched = {};
    function shouldPrefetch(a){
      if(!a) return null;
      var href = a.getAttribute('href');
      if(!href || href.charAt(0) === '#') return null;
      if(a.target === '_blank' || a.hasAttribute('download')) return null;
      if(a.hostname && a.hostname !== window.location.hostname) return null;
      var full = a.href.split('#')[0];
      if(full === window.location.href.split('#')[0]) return null;
      if(prefetched[full]) return null;
      // Skip cart / account / checkout / collection-filter URLs
      if(/\/(cart|checkout|account|orders|carts|admin|search)(\b|\/)/.test(full)) return null;
      if(/[?&](variant|sort_by|filter)/.test(full)) return null;
      return full;
    }
    function prefetch(url){
      prefetched[url] = true;
      var link = document.createElement('link');
      link.rel  = 'prefetch';
      link.href = url;
      link.as   = 'document';
      document.head.appendChild(link);
    }
    function closestLink(target){
      if(!target) return null;
      if(target.nodeType === 1 && target.closest) return target.closest('a');
      return target.parentElement && target.parentElement.closest
        ? target.parentElement.closest('a')
        : null;
    }
    function hoverHandler(e){
      var url = shouldPrefetch(closestLink(e.target));
      if(url) prefetch(url);
    }
    document.addEventListener('mouseenter', hoverHandler, true);
    document.addEventListener('touchstart', hoverHandler, { capture:true, passive:true });

    /* On real click → just show the loader, do NOT block the nav */
    document.addEventListener('click', function(e){
      var a = closestLink(e.target);
      if(!a) return;
      var href = a.getAttribute('href');
      if(!href || href.charAt(0) === '#') return;
      if(a.target === '_blank' || a.hasAttribute('download')) return;
      if(a.hostname && a.hostname !== window.location.hostname) return;
      if(e.metaKey||e.ctrlKey||e.shiftKey||e.button>0) return;
      // Let the browser navigate immediately. Just paint a loader on top.
      startLoader();
    });

    /* When bfcache restores the page, reset the loader */
    window.addEventListener('pageshow', function(){
      navbar.style.transition = 'opacity 220ms ease';
      navbar.style.opacity = '0';
      setTimeout(function(){
        navbar.style.transform = 'scaleX(0)';
        navbar.style.opacity   = '1';
        navState.active = false;
      }, 240);
    });
  }

  /* ─────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────── */
  function ready(fn){
    if(document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  /* ─────────────────────────────────────────────────
     8. MOBILE ANNOUNCE BAR — auto-hide on scroll down,
        reveal on scroll up. Desktop is untouched.
  ───────────────────────────────────────────────── */
  function initAnnounceHide(){
    if(!window.matchMedia('(max-width: 900px)').matches) return;
    var lastY = window.scrollY || 0;
    var ticking = false;
    var threshold = 80;        // only start hiding once user has scrolled past nav height
    var deltaMin  = 6;         // ignore micro-jitter
    function evaluate(){
      var y = window.scrollY || 0;
      var delta = y - lastY;
      if(y < threshold){
        document.body.classList.remove('ae-hide-announce');
      } else if(Math.abs(delta) > deltaMin){
        if(delta > 0) document.body.classList.add('ae-hide-announce');     // scrolling down
        else          document.body.classList.remove('ae-hide-announce');  // scrolling up
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function(){
      if(!ticking){ ticking = true; requestAnimationFrame(evaluate); }
    }, { passive: true });
    /* Re-evaluate if user resizes from desktop down to mobile mid-session */
    window.addEventListener('resize', function(){
      if(!window.matchMedia('(max-width: 900px)').matches){
        document.body.classList.remove('ae-hide-announce');
      }
    });
  }

  ready(function(){
    initCounters();
    initBlurReveal();
    initAutoReveal();
    initLegacyReveal();
    initScrollProgress();
    initMagnetic();
    initTilt();
    initParallax();
    initInstantNav();
    initAnnounceHide();
  });
})();
